import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from 'src/styles/layout.module.css';
import MainLayout from '../../layouts/MainLayout';
import { AppDispatch } from '../../../index';
import { useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { useForm } from 'antd/es/form/Form';
import { useDebounce } from 'react-use';
import { Breadcrumb, Button, Form, Input, Popover, Row, Space, Table, TableProps, Tag, Typography } from 'antd';
import { CrownTwoTone, EditOutlined, ProfileTwoTone, SearchOutlined } from '@ant-design/icons';
import LoaderWrapper from '../../atoms/LoaderWrapper';
import tileStyles from '../../../styles/tile.module.css';
import { useDeviceGroups, useDeviceGroupsState } from '../../../features/deviceGroups/deviceGroups.hooks';
import { createDeviceGroup, fetchDeviceGroups } from '../../../features/deviceGroups/deviceGroups.state';
import { fetchPhyDevices } from '../../../features/phy_devices/state';
import { Device } from '../../../features/devices/devices.types';
import CardStatus from '../../atoms/CardsStatus';
import HeadsetWorkingMode from '../../atoms/HeadsetWorkingMode';
import BatteryStatus from '../../atoms/BatteryStatus/BatteryStatus';
import ComponentRoleProtected from '../../blocks/ComponentRoleProtected';
import { RoleEnum } from '../../../features/users/users.types';
import ConfirmButton from '../../atoms/ConfirmButton';
import { useAppDispatch, useAppSelector } from '../../../hooks/hooks';
import { selectDevices } from '../../../features/phy_devices/selector';
import { PhyDevice } from '../../../features/phy_devices/types';
import { useDevicesTypes } from '../../../features/devicesTypes/devicesTypes.hooks';
import { fetchDeviceTypes } from '../../../features/devicesTypes/devicesTypes.state';

const DevicesPage: React.FC = () => {
  const devices = useAppSelector(selectDevices);
  const dispatch = useAppDispatch();
  const intl = useIntl();

  const [name, setName] = useState<string | undefined>(undefined);

  useDebounce(
    () => {
      if (name !== undefined) {
        dispatch(fetchPhyDevices({ query: name }));
      }
    },
    300,
    [dispatch, name]
  );

  const tableConfig = useMemo<TableProps<PhyDevice>>(() => {
    const result: TableProps<PhyDevice> = {
      dataSource: devices,
      rowKey: values => values.id,
      columns: [
        {
          title: 'Шлем',
          dataIndex: 'label',
          key: 'label',
          render: (label: string, device) => (
            <Space direction={'horizontal'} style={{ justifyContent: 'space-between', width: '100%' }}>
              <Space direction={'vertical'} size={0}>
                <Link to={`/settings/devices/${device.id}`} className={tileStyles.tileName}>
                  <span>{device.companyDevice?.label || device.id}</span>
                </Link>
                <Typography.Text type={'secondary'} style={{ fontSize: '12px' }}>
                  <Space>
                    <span>{device.id}</span>|<span>{device.deviceType.name}</span>|<span>{device.firmware || '-'}</span>
                  </Space>
                </Typography.Text>
              </Space>
            </Space>
          ),
        },
        {
          title: 'Идентификатор',
          dataIndex: 'humanId',
          key: 'humanId',
          render: value => <Typography.Text style={{ fontSize: '12px' }}>{value}</Typography.Text>,
        },
        {
          title: 'Отель',
          dataIndex: 'companyDevice',
          key: 'companyDevice',
          render: (v, r) =>
            r.companyDevice ? (
              <Link to={`/commercial/companies/${r.companyDevice.company.id}`} className={tileStyles.tileName}>
                <span>{r.companyDevice?.company.name}</span>
              </Link>
            ) : (
              '-'
            ),
        },
      ],
    };
    return result;
  }, [devices]);

  return (
    <MainLayout>
      <div className={styles.breadCrumbs}>
        <Breadcrumb>
          <Breadcrumb.Item>
            <Link to='/settings/devices'>
              <Space>
                <ProfileTwoTone />
                Устройства
              </Space>
            </Link>
          </Breadcrumb.Item>
        </Breadcrumb>
      </div>
      <div className={styles.contentArea}>
        <Typography.Title level={2} className={styles.title}>
          Устройства
        </Typography.Title>
        <Space style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, width: '100%' }}>
          <Input
            autoFocus
            allowClear
            placeholder={intl.formatMessage({ id: 'placeholder.search' })}
            prefix={<SearchOutlined />}
            onChange={e => {
              setName(e.target.value);
            }}
          />
        </Space>
        <Table {...tableConfig} size='middle' pagination={{ pageSize: 50 }} />
      </div>
    </MainLayout>
  );
};

const DevicesPageBlock: React.FC = () => {
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchPhyDevices({}));
    dispatch(fetchDeviceTypes({}));
  }, [dispatch]);

  return <DevicesPage />;
};

export default DevicesPageBlock;
